import type { FC } from 'react';
import {
  type PopoverProps,
  Dialog,
  DialogTrigger,
  IconButton,
  Popover,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';

import { HealthCheckButton } from './HealthCheckButton/HealthCheckButton';
import { HealthCheckCard } from './HealthCheckCard/HealthCheckCard';
import type { HealthCheckOverallStatus } from './types';

import styles from './HealthCheck.module.css';

export type HealthCheckProps = {
  status: HealthCheckOverallStatus;
  children: React.ReactNode;
  /**
   * Optional URL to the documentation page in format 'home/experimentation/health-checks'.
   * If not provided, the documentation link will not be displayed.
   * */
  documentationUrl?: string;
  /** Optional callback when button is clicked to open the card, e.g. for event tracking */
  onButtonClick?: () => void;
  /** Optional callback when documentation link is clicked, e.g. for event tracking */
  onDocumentationLinkClick?: () => void;
  /** Callback when reload button is clicked on API error. Can be used for both error and partial-error states. */
  onReload?: () => void;
  title?: string;
  subtitle?: string;
  /** Placement of the popover. Defaults to 'bottom end'. */
  popoverPlacement?: PopoverProps['placement'];
};
/**
 *
 * Health check is a button with popover that displays a list of health checks e.g. for guarded rollouts, metrics multi-armed bandits, or experiments.
 *
 * For `children`, you can use `HealthCheckItem`, `HealthCheckFlag`, `HealthCheckMetrics`
 */
export const HealthCheck: FC<HealthCheckProps> = ({
  status,
  title = 'Health check',
  subtitle = '',
  documentationUrl,
  onButtonClick,
  onDocumentationLinkClick,
  onReload,
  children,
  popoverPlacement = 'bottom end',
}) => (
  <div className={styles.container}>
    <DialogTrigger>
      <HealthCheckButton status={status} onPress={onButtonClick} />
      {status === 'loading' ? null : (
        <Popover placement={popoverPlacement} className={styles.popover}>
          <Dialog>
            <HealthCheckCard
              title={title}
              subtitle={subtitle}
              status={status}
              documentationUrl={documentationUrl}
              onDocumentationLinkClick={onDocumentationLinkClick}
            >
              {children}
            </HealthCheckCard>
          </Dialog>
        </Popover>
      )}
    </DialogTrigger>
    {['error', 'partial-error'].includes(status) && onReload && (
      <TooltipTrigger>
        <IconButton
          size="small"
          data-status="error"
          aria-label="Retry health check"
          icon="sync"
          variant="minimal"
          onClick={onReload}
        />
        <Tooltip>Reload</Tooltip>
      </TooltipTrigger>
    )}
  </div>
);
